import api, { ListResponse } from "features/api";

/** Who created or last changed a secret; null when that user is gone. */
export type UserRef = { id: number; display_name: string } | null;

export interface Secret {
  id: number;
  /** Immutable once created, like `requires_encryption`. */
  name: string;
  slug: string;
  description: string;
  requires_encryption: boolean;
  /**
   * Present only on a non-sensitive secret. The API leaves the key out
   * entirely (not null) when `requires_encryption` is true.
   */
  value?: string;
  created_on: string;
  created_by: UserRef;
  updated_on: string | null;
  updated_by: UserRef;
}

/** The environment's KMS public key, for encrypting a value in the browser. */
export interface SecretsPublicKey {
  key_id: string;
  /** Base64 DER SubjectPublicKeyInfo. */
  public_key: string;
  algorithm: "RSAES_OAEP_SHA_256";
  max_bytes: number;
}

export type SecretAuditEventType =
  | "secret_created"
  | "value_changed"
  | "description_changed"
  | "integration_changed";

export interface SecretAuditEvent {
  id: number;
  event: SecretAuditEventType;
  event_label: string;
  /** Blank for an integration change that unbound a field. */
  secret_name: string;
  /** Set only on `integration_changed`. */
  integration_label: string | null;
  /**
   * Set only on `integration_changed`; null on every other event, and on that
   * one when the field was unbound before (old) or after (new) the change.
   */
  old_secret_name: string | null;
  new_secret_name: string | null;
  user_display: string;
  created_on: string;
}

export interface CreateSecretParams {
  name: string;
  description: string;
  requires_encryption: boolean;
  /** Plaintext for a non-sensitive secret; base64 ciphertext otherwise. */
  value: string;
}

export interface UpdateSecretParams {
  id: number;
  description?: string;
  /** Plaintext for a non-sensitive secret; base64 ciphertext otherwise. */
  value?: string;
}

/** The backend's limit on a value, in UTF-8 bytes (KMS RSA_4096 OAEP-SHA-256). */
export const SECRET_VALUE_MAX_BYTES = 446;

const LIST_TAG = { type: "Secret" as const, id: "LIST" };

// A secret write also changes the audit log and the integrations' drop-down
// (its option names), so it invalidates all three.
const WRITE_TAGS = [LIST_TAG, "SecretAudit" as const, "Integrations" as const];

export const apiWithSecrets = api.injectEndpoints({
  endpoints: (builder) => ({
    listSecrets: builder.query<Secret[], void>({
      query: () => `/secrets/`,
      providesTags: (result) => [
        LIST_TAG,
        ...(result ?? []).map(({ id }) => ({ type: "Secret" as const, id })),
      ],
    }),

    createSecret: builder.mutation<Secret, CreateSecretParams>({
      query: (body) => ({ url: `/secrets/`, method: "POST", body }),
      invalidatesTags: WRITE_TAGS,
    }),

    updateSecret: builder.mutation<Secret, UpdateSecretParams>({
      query: ({ id, ...body }) => ({
        url: `/secrets/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Secret", id },
        ...WRITE_TAGS,
      ],
    }),

    /** Fetched afresh before each encryption, never served from the cache. */
    getSecretsPublicKey: builder.query<SecretsPublicKey, void>({
      query: () => `/secrets/public-key/`,
      keepUnusedDataFor: 0,
    }),

    listSecretAudit: builder.query<ListResponse<SecretAuditEvent>, number>({
      query: (page) => ({ url: `/secrets/audit/`, params: { page } }),
      providesTags: ["SecretAudit"],
    }),
  }),
});

export const {
  useListSecretsQuery,
  useCreateSecretMutation,
  useUpdateSecretMutation,
  useLazyGetSecretsPublicKeyQuery,
  useListSecretAuditQuery,
} = apiWithSecrets;
